import { Outlet } from 'react-router'
import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Command, LogOut, Moon, Sparkles, Sun, User } from 'lucide-react'
import { PortalSidebarNav } from './portal-sidebar-nav'
import { PortalBottomTabs } from './portal-bottom-tabs'
import { CommandPalette } from '@/components/shared/command-palette'
import { FooterBar } from '@/components/shared/footer-bar'
import { useAuth } from '@/providers/auth-provider'
import { useTheme } from '@/providers/theme-provider'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

const COLLAPSE_BREAKPOINT = 1024
const MOBILE_BREAKPOINT = 768

/**
 * PortalLayout — client-facing portal shell.
 *
 * Differences from AdminLayout:
 * - Greeting in header: "Good morning/afternoon/evening, {name}"
 * - Notification bell (unread count — data from query)
 * - Mobile bottom tab bar below 768px (sidebar hidden on mobile)
 * - Padding: p-4 on mobile, p-8 on desktop
 */
export function PortalLayout() {
  const { user, logout } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()
  const [commandOpen, setCommandOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  )
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < COLLAPSE_BREAKPOINT
  )

  useEffect(() => {
    const mqMobile = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const mqCollapse = window.matchMedia(`(max-width: ${COLLAPSE_BREAKPOINT - 1}px)`)
    const handleMobile = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    const handleCollapse = (e: MediaQueryListEvent) => setCollapsed(e.matches)
    mqMobile.addEventListener('change', handleMobile)
    mqCollapse.addEventListener('change', handleCollapse)
    return () => {
      mqMobile.removeEventListener('change', handleMobile)
      mqCollapse.removeEventListener('change', handleCollapse)
    }
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCommandOpen(true)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  return (
    <div className="flex h-screen overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(3,102,102,0.16),_transparent_26%),radial-gradient(circle_at_top_right,_rgba(128,239,128,0.11),_transparent_24%),linear-gradient(180deg,_rgba(3,102,102,0.04),_transparent_16%),linear-gradient(180deg,_var(--color-background),_var(--color-background))]">
      {/* ── Sidebar (hidden on mobile — replaced by bottom tabs) ──────── */}
      {!isMobile && (
        <aside
          className={cn(
            'flex shrink-0 flex-col border-r border-sidebar-border/70 bg-[linear-gradient(180deg,rgba(247,255,250,0.95),rgba(240,252,248,0.92))] transition-all duration-200',
            collapsed ? 'w-14' : 'w-56'
          )}
        >
          {/* Sidebar header */}
          <div className={cn('flex h-20 items-center border-b border-sidebar-border/60 px-3', collapsed && 'justify-center')}>
            {!collapsed && (
              <div className="w-full rounded-2xl border border-sidebar-border/60 bg-white/85 px-4 py-3 shadow-[0_20px_40px_rgba(3,102,102,0.08)]">
                <div className="flex items-center justify-between gap-3">
                  <div className="space-y-2">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-sidebar-foreground/50">Managed portal</p>
                  </div>
                </div>
              </div>
            )}
            {collapsed && (
              <div className="h-8 w-8" />
            )}
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto">
            <PortalSidebarNav collapsed={collapsed} />
          </div>

          {!collapsed && (
            <div className="px-3 pb-3">
              <a
                href="https://m.s360dash.com"
                className="block rounded-2xl border border-sidebar-border/60 bg-white/80 p-4 text-sidebar-foreground shadow-[0_18px_40px_rgba(3,102,102,0.08)] transition-transform hover:-translate-y-0.5"
              >
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-sidebar-foreground/55">
                  <Sparkles className="h-3.5 w-3.5" />
                  Mobile app
                </div>
                <img
                  src="/branding/app-phone-home_768w.avif"
                  srcSet="/branding/app-phone-home_480w.avif 480w, /branding/app-phone-home_677w.avif 677w, /branding/app-phone-home_768w.avif 768w, /branding/app-phone-home_1280w.avif 1280w, /branding/app-phone-home_1920w.avif 1920w"
                  sizes="240px"
                  alt="360Dash mobile app on phone"
                  className="mt-4 w-full rounded-[1rem] border border-sidebar-border/50 object-cover"
                />
                <p className="mt-4 text-sm font-medium">download our app on your phone</p>
                <p className="mt-1 text-xs leading-5 text-sidebar-foreground/68">
                  go to m.s360dash.com
                </p>
              </a>
            </div>
          )}

          {/* Collapse toggle */}
          <div className="border-t border-sidebar-border p-2">
            <button
              onClick={() => setCollapsed(c => !c)}
              className="flex w-full items-center justify-center rounded-xl p-2 text-sidebar-foreground/80 transition-colors hover:bg-white/70 hover:text-sidebar-foreground"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
          </div>
        </aside>
      )}

      {/* ── Main area ────────────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Header */}
        <header className="shrink-0 border-b border-border/70 bg-background/82 px-4 py-4 backdrop-blur md:px-6">
          <div className="flex h-full items-center justify-between gap-4">
            <div className="space-y-2">
              <img src="/branding/360dashwithboxsmall.svg" alt="360Dash" className="h-6 w-auto" />
              <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground shadow-sm">
                <Sparkles className="h-3.5 w-3.5" />
                Client portal
              </div>
              <p className="text-base font-semibold text-foreground">Client workspace</p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="hidden gap-2 rounded-full border border-border/70 bg-background/80 px-3 text-muted-foreground shadow-sm sm:flex"
                onClick={() => setCommandOpen(true)}
              >
                <Command className="h-3.5 w-3.5" />
                <span className="text-xs">Command</span>
                <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] text-muted-foreground">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
                aria-label="Toggle theme"
              >
                {resolvedTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full" aria-label="User menu">
                    <User className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="px-2 py-1.5 text-sm">
                    <p className="font-medium">{user?.email}</p>
                    <p className="text-xs text-muted-foreground">Client</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content — extra bottom padding on mobile to clear the tab bar */}
        <main className="flex-1 overflow-y-auto">
          <div className={cn('mx-auto max-w-7xl', isMobile ? 'p-4 pb-28' : 'p-4 md:p-6 lg:p-8')}>
            <Outlet />
          </div>
        </main>
        <FooterBar />
      </div>

      {/* Mobile bottom tab bar */}
      <PortalBottomTabs />

      {/* Command palette */}
      <CommandPalette
        open={commandOpen}
        onOpenChange={setCommandOpen}
        role="client"
      />
    </div>
  )
}
